import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
// Editorial theme colors
const COLORS = {
  cream: '#FAF8F5',
  charcoal: '#2D2A26',
  terracotta: '#C45D3A',
  border: '#2D2A26'
};

const normalizeCategoryList = (rawValues, limit = 20) => {
  if (!Array.isArray(rawValues)) return [];
  const output = [];
  const seen = new Set();
  rawValues.forEach((raw) => {
    const value = typeof raw === 'string' ? raw : (raw?.name || raw?.displayName || '');
    const normalized = String(value || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    output.push(normalized);
  });
  return output.slice(0, limit);
};

const SectionContainer = styled(Box)({
  backgroundColor: COLORS.cream,
  borderBottom: `1px solid ${COLORS.charcoal}`
});

const SectionHeader = styled(Box)({
  padding: '48px 64px 32px',
  borderBottom: `1px solid ${COLORS.charcoal}`,
  '@media (max-width: 768px)': {
    padding: '32px 20px 24px'
  }
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '42px',
  fontWeight: 700,
  color: COLORS.charcoal,
  letterSpacing: '-1px',
  '@media (max-width: 768px)': {
    fontSize: '32px'
  }
});

const SectionSubtitle = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: COLORS.charcoal,
  opacity: 0.6,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginTop: '8px'
});

const ContentWrapper = styled(Box)({
  padding: '48px 64px',
  '@media (max-width: 768px)': {
    padding: '32px 20px'
  }
});

const CategorySection = styled(Box)({
  marginBottom: '32px',
  '&:last-child': {
    marginBottom: 0
  }
});

const CategoryLabel = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: COLORS.charcoal,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginBottom: '16px',
  opacity: 0.7
});

const TagsWrapper = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  justifyContent: 'flex-start'
});

const Tag = styled(Box)({
  padding: '12px 24px',
  border: `1px solid ${COLORS.charcoal}`,
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  color: COLORS.charcoal,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  transition: 'all 0.2s ease',
  cursor: 'default',
  '&:hover': {
    backgroundColor: COLORS.charcoal,
    color: COLORS.cream
  }
});

const CategoryTag = styled(Tag)({
  backgroundColor: `${COLORS.terracotta}15`,
  borderColor: COLORS.terracotta,
  color: COLORS.terracotta,
  '&:hover': {
    backgroundColor: COLORS.terracotta,
    color: COLORS.cream
  }
});

const ServiceTag = styled(Tag)({
  backgroundColor: `${COLORS.charcoal}08`,
  '&:hover': {
    backgroundColor: COLORS.charcoal,
    color: COLORS.cream
  }
});

const AttributeTag = styled(Tag)({
  border: `1px dashed ${COLORS.charcoal}`,
  opacity: 0.8,
  '&:hover': {
    opacity: 1,
    backgroundColor: COLORS.charcoal,
    color: COLORS.cream,
    borderStyle: 'solid'
  }
});

const EditorialCategoryTags = ({ businessInfo, categories, services, products, keywordsData }) => {
  // Use AI-generated categories if available (same as free website), otherwise fall back to GMB categories
  const aiRelatedCategories = normalizeCategoryList([
    ...(Array.isArray(businessInfo?.aiRelatedCategories) ? businessInfo.aiRelatedCategories : []),
    ...(Array.isArray(categories) ? categories : [])
  ], 12);

  const fallbackGmbCategories = normalizeCategoryList([
    businessInfo?.category,
    ...(Array.isArray(businessInfo?.gmbCategories) ? businessInfo.gmbCategories : [])
  ], 8);

  const allCategories = aiRelatedCategories.length > 0 ? aiRelatedCategories : fallbackGmbCategories;

  // Collect tags from keywords data (from Performance page), services, and products
  const allTags = new Set();

  // Add keywords from keywordsData (Performance page keywords)
  // Keywords have structure: { searchTerm: string, searchVolume: number, ... }
  if (Array.isArray(keywordsData) && keywordsData.length > 0) {
    keywordsData.forEach(keyword => {
      if (keyword?.searchTerm) {
        allTags.add(keyword.searchTerm);
      } else if (typeof keyword === 'string') {
        allTags.add(keyword);
      }
    });
  }

  // Add services - only if user has real services (not mock/placeholder)
  // Check if services have actual user data (id from user input, not mock data)
  if (Array.isArray(services) && services.length > 0) {
    services.forEach(service => {
      // Only add if it's real user data (has id or has actual name)
      const hasRealData = service?.id || (service?.name && service.name !== 'placeholder');
      if (hasRealData) {
        if (service?.name) allTags.add(service.name);
        else if (service?.title) allTags.add(service.title);
      }
    });
  }

  // Add products - only if user has real products (not mock/placeholder)
  if (Array.isArray(products) && products.length > 0) {
    products.forEach(product => {
      // Only add if it's real user data
      const hasRealData = product?.id || (product?.name && product.name !== 'placeholder');
      if (hasRealData) {
        if (product?.name) allTags.add(product.name);
        else if (product?.title) allTags.add(product.title);
      }
    });
  }

  const tagsArray = Array.from(allTags).slice(0, 15);

  // Collect attribute tags
  const attributeTags = [];
  if (businessInfo?.hasWifi) attributeTags.push('WiFi Available');
  if (businessInfo?.petFriendly) attributeTags.push('Pet Friendly');
  if (businessInfo?.outdoorSeating) attributeTags.push('Outdoor Seating');
  if (businessInfo?.parking) attributeTags.push('Parking Available');
  if (businessInfo?.wheelchairAccessible) attributeTags.push('Wheelchair Accessible');

  const hasCategories = allCategories.length > 0;
  const hasTags = tagsArray.length > 0;
  const hasAttributes = attributeTags.length > 0;

  if (!hasCategories && !hasTags && !hasAttributes) {
    return null;
  }

  return (
    <SectionContainer id="tags">
      <SectionHeader>
        <SectionSubtitle>Discover More</SectionSubtitle>
        <SectionTitle>Tags & Categories</SectionTitle>
      </SectionHeader>

      <ContentWrapper>
        {hasCategories && (
          <CategorySection>
            <CategoryLabel>Categories</CategoryLabel>
            <TagsWrapper>
              {allCategories.map((cat, index) => (
                <CategoryTag key={`cat-${index}`}>{cat}</CategoryTag>
              ))}
            </TagsWrapper>
          </CategorySection>
        )}

        {hasTags && (
          <CategorySection>
            <CategoryLabel>Tags</CategoryLabel>
            <TagsWrapper>
              {tagsArray.map((tag, index) => (
                <ServiceTag key={`tag-${index}`}>{tag}</ServiceTag>
              ))}
            </TagsWrapper>
          </CategorySection>
        )}

        {hasAttributes && (
          <CategorySection>
            <CategoryLabel>Features & Amenities</CategoryLabel>
            <TagsWrapper>
              {attributeTags.map((attr, index) => (
                <AttributeTag key={`attr-${index}`}>{attr}</AttributeTag>
              ))}
            </TagsWrapper>
          </CategorySection>
        )}
      </ContentWrapper>
    </SectionContainer>
  );
};

export default EditorialCategoryTags;
